import React, { useState } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import {
  QrCode, Download, Plus, Trash2, Pencil, Save, X, Users, MapPin, Eye, EyeOff, Layers
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const TablesTab = ({ tables, onRefresh, generateQR, qrData, adminRole }) => {
  const [showNew, setShowNew] = useState(false);
  const [showBulk, setShowBulk] = useState(false);
  const [newNumber, setNewNumber] = useState('');
  const [newName, setNewName] = useState('');
  const [newCapacity, setNewCapacity] = useState('4');
  const [newZone, setNewZone] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});
  const [qrDialogTable, setQrDialogTable] = useState(null);
  const [bulkCount, setBulkCount] = useState('10');
  const [bulkCapacity, setBulkCapacity] = useState('4');
  const [bulkZone, setBulkZone] = useState('');
  const [bulkStart, setBulkStart] = useState('1');
  const [bulkLoading, setBulkLoading] = useState(false);

  const activeTables = tables.filter(t => t.is_active !== false);
  const inactiveTables = tables.filter(t => t.is_active === false);
  const zones = [...new Set(tables.map(t => t.zone).filter(Boolean))];

  const createTable = async () => {
    if (!newNumber) return;
    try {
      await axios.post(`${API}/admin/tables?table_number=${newNumber}&name=${encodeURIComponent(newName || `Table ${newNumber}`)}&capacity=${newCapacity || 4}&zone=${encodeURIComponent(newZone)}`);
      setNewNumber('');
      setNewName('');
      setNewCapacity('4');
      setNewZone('');
      setShowNew(false);
      onRefresh();
    } catch (e) {
      alert(e.response?.data?.detail || 'Erreur');
    }
  };

  const createBulk = async () => {
    const count = parseInt(bulkCount) || 0;
    const capacity = parseInt(bulkCapacity) || 4;
    const start = parseInt(bulkStart) || 1;
    if (count < 1) return;
    setBulkLoading(true);
    try {
      const res = await axios.post(`${API}/admin/tables/bulk`, {
        count, capacity, zone: bulkZone, start_number: start
      });
      const msg = `${res.data.created_count} table(s) creee(s)${res.data.skipped?.length ? ` | ${res.data.skipped.length} ignoree(s) (deja existantes)` : ''}`;
      alert(msg);
      setShowBulk(false);
      setBulkCount('10');
      setBulkCapacity('4');
      setBulkZone('');
      setBulkStart('1');
      onRefresh();
    } catch (e) {
      alert(e.response?.data?.detail || 'Erreur');
    } finally {
      setBulkLoading(false);
    }
  };

  const updateTable = async (tableId) => {
    try {
      await axios.patch(`${API}/admin/tables/${tableId}`, editData);
      setEditingId(null);
      setEditData({});
      onRefresh();
    } catch (e) {
      alert(e.response?.data?.detail || 'Erreur');
    }
  };

  const deleteTable = async (tableId) => {
    if (!window.confirm(`Supprimer la table ${tableId} ? Elle sera aussi retiree des serveurs assignes.`)) return;
    try {
      await axios.delete(`${API}/admin/tables/${tableId}`);
      onRefresh();
    } catch (e) {
      alert(e.response?.data?.detail || 'Erreur');
    }
  };

  const toggleActive = async (tableId, currentActive) => {
    try {
      await axios.patch(`${API}/admin/tables/${tableId}`, { is_active: !currentActive });
      onRefresh();
    } catch (e) {
      alert(e.response?.data?.detail || 'Erreur');
    }
  };

  const openQR = (table) => {
    setQrDialogTable(table);
    generateQR(table.number);
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-white rounded-2xl p-4 border border-[#E5DCD3] flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div>
          <h3 className="font-semibold text-[#2D241D]" data-testid="tables-title">
            Tables ({activeTables.length} actives{inactiveTables.length > 0 ? `, ${inactiveTables.length} desactivees` : ''})
          </h3>
          <p className="text-xs text-[#6E5E52]">Gerez vos tables, zones et QR codes</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => setShowBulk(true)}
            className="bg-[#1B3A4B] hover:bg-[#142D3B] text-white rounded-xl" data-testid="bulk-create-tables-btn">
            <Layers className="w-4 h-4 mr-2" /> Creation en masse
          </Button>
          <Button onClick={() => setShowNew(true)}
            className="bg-[#E16833] hover:bg-[#C95A2A] text-white rounded-xl" data-testid="add-table-btn">
            <Plus className="w-4 h-4 mr-2" /> Nouvelle table
          </Button>
        </div>
      </div>

      {/* Bulk creation form */}
      {showBulk && (
        <div className="bg-white rounded-2xl p-4 border-2 border-[#1B3A4B] space-y-3" data-testid="bulk-table-form">
          <h4 className="font-semibold text-[#2D241D] flex items-center gap-2">
            <Layers className="w-5 h-5 text-[#1B3A4B]" /> Creation en masse de tables
          </h4>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div>
              <label className="text-xs font-medium text-[#6E5E52] mb-1 block">Nombre de tables *</label>
              <Input type="number" value={bulkCount} onChange={(e) => setBulkCount(e.target.value)}
                placeholder="10" min="1" max="100"
                className="rounded-xl border-[#E5DCD3] text-[#2D241D] bg-white" data-testid="bulk-count" />
            </div>
            <div>
              <label className="text-xs font-medium text-[#6E5E52] mb-1 block">Numero de depart</label>
              <Input type="number" value={bulkStart} onChange={(e) => setBulkStart(e.target.value)}
                placeholder="1" min="1"
                className="rounded-xl border-[#E5DCD3] text-[#2D241D] bg-white" data-testid="bulk-start" />
            </div>
            <div>
              <label className="text-xs font-medium text-[#6E5E52] mb-1 block">Places par table</label>
              <Input type="number" value={bulkCapacity} onChange={(e) => setBulkCapacity(e.target.value)}
                placeholder="4"
                className="rounded-xl border-[#E5DCD3] text-[#2D241D] bg-white" data-testid="bulk-capacity" />
            </div>
            <div>
              <label className="text-xs font-medium text-[#6E5E52] mb-1 block">Zone (optionnel)</label>
              <Input value={bulkZone} onChange={(e) => setBulkZone(e.target.value)}
                placeholder="ex: Terrasse"
                className="rounded-xl border-[#E5DCD3] text-[#2D241D] bg-white" data-testid="bulk-zone"
                list="bulk-zone-suggestions" />
              {zones.length > 0 && (
                <datalist id="bulk-zone-suggestions">
                  {zones.map(z => <option key={z} value={z} />)}
                </datalist>
              )}
            </div>
          </div>
          <p className="text-xs text-[#6E5E52]">
            Cela va creer les tables {bulkStart || 1} a {(parseInt(bulkStart) || 1) + (parseInt(bulkCount) || 0) - 1} avec {bulkCapacity || 4} places chacune.
            Les tables existantes seront ignorees.
          </p>
          <div className="flex gap-2 justify-end">
            <Button onClick={() => setShowBulk(false)} variant="outline"
              className="rounded-xl border-[#E5DCD3] text-[#6E5E52] bg-white hover:bg-[#F3EDE5]">
              Annuler
            </Button>
            <Button onClick={createBulk} disabled={!bulkCount || bulkLoading}
              className="bg-[#1B3A4B] hover:bg-[#142D3B] text-white rounded-xl" data-testid="save-bulk-tables-btn">
              <Save className="w-4 h-4 mr-2" /> {bulkLoading ? 'Creation...' : 'Creer les tables'}
            </Button>
          </div>
        </div>
      )}

      {/* New table form */}
      {showNew && (
        <div className="bg-white rounded-2xl p-4 border-2 border-[#E16833] space-y-3" data-testid="new-table-form">
          <h4 className="font-semibold text-[#2D241D]">Ajouter une table</h4>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div>
              <label className="text-xs font-medium text-[#6E5E52] mb-1 block">Numero *</label>
              <Input type="number" value={newNumber} onChange={(e) => setNewNumber(e.target.value)}
                placeholder="ex: 7" className="rounded-xl border-[#E5DCD3] text-[#2D241D] bg-white" data-testid="new-table-number" />
            </div>
            <div>
              <label className="text-xs font-medium text-[#6E5E52] mb-1 block">Nom (optionnel)</label>
              <Input value={newName} onChange={(e) => setNewName(e.target.value)}
                placeholder="ex: Terrasse 1" className="rounded-xl border-[#E5DCD3] text-[#2D241D] bg-white" data-testid="new-table-name" />
            </div>
            <div>
              <label className="text-xs font-medium text-[#6E5E52] mb-1 block">Places</label>
              <Input type="number" value={newCapacity} onChange={(e) => setNewCapacity(e.target.value)}
                placeholder="4" className="rounded-xl border-[#E5DCD3] text-[#2D241D] bg-white" data-testid="new-table-capacity" />
            </div>
            <div>
              <label className="text-xs font-medium text-[#6E5E52] mb-1 block">Zone</label>
              <Input value={newZone} onChange={(e) => setNewZone(e.target.value)}
                placeholder="ex: Terrasse" className="rounded-xl border-[#E5DCD3] text-[#2D241D] bg-white" data-testid="new-table-zone"
                list="zone-suggestions" />
              {zones.length > 0 && (
                <datalist id="zone-suggestions">
                  {zones.map(z => <option key={z} value={z} />)}
                </datalist>
              )}
            </div>
          </div>
          <div className="flex gap-2 justify-end">
            <Button onClick={() => setShowNew(false)} variant="outline"
              className="rounded-xl border-[#E5DCD3] text-[#6E5E52] bg-white hover:bg-[#F3EDE5]">
              Annuler
            </Button>
            <Button onClick={createTable} disabled={!newNumber}
              className="bg-[#0A422D] hover:bg-[#083323] text-white rounded-xl" data-testid="save-new-table-btn">
              <Save className="w-4 h-4 mr-2" /> Creer
            </Button>
          </div>
        </div>
      )}

      {/* Tables grid */}
      <div className="grid gap-3 grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
        {tables.sort((a, b) => a.number - b.number).map(table => {
          const isEditing = editingId === table.id;
          const isInactive = table.is_active === false;

          return (
            <div key={table.id}
              className={`bg-white rounded-2xl border overflow-hidden transition-all ${isInactive ? 'border-red-200 opacity-50' : 'border-[#E5DCD3]'} ${isEditing ? 'ring-2 ring-[#D4A853]' : ''}`}
              data-testid={`table-card-${table.id}`}>

              {/* Table header */}
              <div className={`px-4 py-3 ${isInactive ? 'bg-red-50' : 'bg-[#F3EDE5]'}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-lg font-bold ${isInactive ? 'bg-red-100 text-red-500' : 'bg-[#0A422D] text-white'}`}>
                      {table.number}
                    </div>
                    <div>
                      {isEditing ? (
                        <Input value={editData.name ?? table.name ?? ''} onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                          className="h-6 text-xs rounded border-[#E5DCD3] w-24 text-[#2D241D] bg-white" data-testid={`edit-table-name-${table.id}`} />
                      ) : (
                        <p className="text-sm font-semibold text-[#2D241D]">{table.name || `Table ${table.number}`}</p>
                      )}
                      {table.zone && !isEditing && (
                        <p className="text-[10px] text-[#6E5E52] flex items-center gap-0.5">
                          <MapPin className="w-2.5 h-2.5" /> {table.zone}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Table details */}
              <div className="px-4 py-3 space-y-2">
                {isEditing ? (
                  <div className="space-y-2">
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="text-[10px] text-[#6E5E52]">Places</label>
                        <Input type="number" value={editData.capacity ?? table.capacity ?? 4}
                          onChange={(e) => setEditData({ ...editData, capacity: parseInt(e.target.value) || 4 })}
                          className="h-7 text-xs rounded border-[#E5DCD3] text-[#2D241D] bg-white" data-testid={`edit-table-capacity-${table.id}`} />
                      </div>
                      <div>
                        <label className="text-[10px] text-[#6E5E52]">Zone</label>
                        <Input value={editData.zone ?? table.zone ?? ''}
                          onChange={(e) => setEditData({ ...editData, zone: e.target.value })}
                          className="h-7 text-xs rounded border-[#E5DCD3] text-[#2D241D] bg-white" placeholder="Zone"
                          data-testid={`edit-table-zone-${table.id}`} />
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <Button size="sm" onClick={() => updateTable(table.id)}
                        className="flex-1 bg-[#0A422D] text-white rounded-lg h-7 text-[10px]" data-testid={`save-table-${table.id}`}>
                        <Save className="w-3 h-3 mr-0.5" /> Sauver
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => { setEditingId(null); setEditData({}); }}
                        className="h-7 px-2 text-[#6E5E52]"><X className="w-3 h-3" /></Button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="flex items-center justify-between text-xs text-[#6E5E52]">
                      <span className="flex items-center gap-1"><Users className="w-3 h-3" /> {table.capacity || 4} places</span>
                      {isInactive && <span className="text-red-500 font-semibold">Desactivee</span>}
                    </div>
                    <div className="flex gap-1">
                      <button onClick={() => openQR(table)}
                        className="flex-1 h-8 rounded-lg bg-[#E16833]/10 text-[#E16833] flex items-center justify-center gap-1 text-xs font-medium hover:bg-[#E16833] hover:text-white transition-colors"
                        data-testid={`qr-btn-${table.id}`}>
                        <QrCode className="w-3.5 h-3.5" /> QR
                      </button>
                      <button onClick={() => { setEditingId(table.id); setEditData({ name: table.name, capacity: table.capacity, zone: table.zone }); }}
                        className="h-8 w-8 rounded-lg bg-[#F3EDE5] text-[#6E5E52] flex items-center justify-center hover:bg-[#0A422D] hover:text-white transition-colors"
                        data-testid={`edit-table-${table.id}`}>
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={() => toggleActive(table.id, table.is_active !== false)}
                        className={`h-8 w-8 rounded-lg flex items-center justify-center transition-colors ${isInactive ? 'bg-[#2D6A4F]/10 text-[#2D6A4F] hover:bg-[#2D6A4F] hover:text-white' : 'bg-red-50 text-red-400 hover:bg-red-500 hover:text-white'}`}
                        data-testid={`toggle-table-${table.id}`}>
                        {isInactive ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
                      </button>
                      {adminRole === 'admin' && (
                        <button onClick={() => deleteTable(table.id)}
                          className="h-8 w-8 rounded-lg bg-red-50 text-red-400 flex items-center justify-center hover:bg-red-500 hover:text-white transition-colors"
                          data-testid={`delete-table-${table.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {tables.length === 0 && (
        <div className="text-center py-12 text-[#6E5E52] bg-white rounded-2xl border border-[#E5DCD3]">
          <QrCode className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p className="font-medium">Aucune table configuree</p>
          <p className="text-xs mt-1">Ajoutez vos tables pour generer les QR codes</p>
        </div>
      )}

      {/* QR Code Dialog */}
      <Dialog open={!!qrDialogTable} onOpenChange={(open) => !open && setQrDialogTable(null)}>
        <DialogContent className="max-w-sm bg-white rounded-3xl admin-theme">
          <DialogHeader>
            <DialogTitle className="text-center text-[#2D241D]">
              {qrDialogTable?.name || `Table ${qrDialogTable?.number}`} - QR Code
            </DialogTitle>
          </DialogHeader>
          {qrData && qrDialogTable && qrData.table_number === qrDialogTable.number && (
            <div className="text-center p-4">
              <img src={qrData.qr_code} alt={`QR Code Table ${qrDialogTable.number}`}
                className="w-48 h-48 mx-auto border-4 border-[#0A422D] rounded-2xl"
                data-testid={`qr-image-${qrDialogTable.number}`} />
              <p className="text-sm text-[#6E5E52] mt-4 break-all">{qrData.url}</p>
              <Button onClick={() => {
                const link = document.createElement('a');
                link.href = qrData.qr_code;
                link.download = `table-${qrDialogTable.number}-qr.png`;
                link.click();
              }} className="mt-4 btn-primary rounded-full" data-testid="download-qr-btn">
                <Download className="w-4 h-4 mr-2" /> Telecharger QR
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
